/* Apply the displayed offer before shopping so gift progress uses discounted spend. */
(() => {
  'use strict';
  const scope = 'form[data-testid="klaviyo-form-X5sknC"]';
  const collection = '/collections/shop-your-spin-prize';
  const validCode = (code) => /^(?:SBSPIN(?:10|15|20)-[A-Z0-9]{6,64}|REVEALBACKUP)$/.test(code) && !/-(?:PREVIEW|PLACEHOLDER|EXAMPLE|TEST)$/.test(code);
  const visible = (element) => {
    if (!element.getClientRects().length) return false;
    for (let current = element; current; current = current.parentElement) {
      const style = getComputedStyle(current);
      if (current.hidden || current.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
    }
    return true;
  };
  function displayedCode(form) {
    const found = new Set();
    const collect = (text) => {
      for (const match of text.matchAll(/\bSBSPIN(?:10|15|20)-[A-Za-z0-9_-]+/g)) {
        if (validCode(match[0])) found.add(match[0]);
      }
    };
    const walker = document.createTreeWalker(form, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const parent = walker.currentNode.parentElement;
      if (parent && !parent.closest('[data-sb-coupon-message]') && visible(parent)) collect(walker.currentNode.textContent);
    }
    form.querySelectorAll('input[readonly], textarea[readonly]').forEach((input) => {
      if (visible(input)) collect(input.value);
    });
    return found.size === 1 ? [...found][0] : null;
  }
  function destination(value) {
    if (!value || value.startsWith('//') || /[\\\r\n]/.test(value)) return null;
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/^\/(?:cart\/?$|collections\/)/.test(url.pathname)) return null;
      url.searchParams.delete('sb_apply_coupon');
      url.searchParams.delete('sb_coupon_redirect');
      return url.pathname + url.search + url.hash;
    } catch (_) { return null; }
  }
  function restoreItems(value) {
    if (typeof value !== 'string' || value.length > 5000) return null;
    const entries = value.split(',').filter((entry) => entry !== '');
    if (!entries.length || entries.length > 100) return null;
    const gifts = new Set(['67616258359511', '67616356073687', '67617292484823']);
    const quantities = new Map();
    for (const entry of entries) {
      const match = /^(\d{1,16}):(\d{1,4})$/.exec(entry);
      if (!match) return null;
      const variant = Number(match[1]);
      const quantity = Number(match[2]);
      if (!Number.isSafeInteger(variant) || variant <= 0 || !Number.isSafeInteger(quantity) || quantity <= 0 || quantity > 999) return null;
      const key = String(variant);
      if (gifts.has(key)) return null;
      const total = (quantities.get(key) || 0) + quantity;
      if (total > 999) return null;
      quantities.set(key, total);
    }
    return [...quantities].map(([variant, quantity]) => variant + ':' + quantity).join(',');
  }
  function hasPaidItems(cart) {
    const gifts = new Set(['67616258359511', '67616356073687', '67617292484823']);
    return cart.items.some((item) => !gifts.has(String(item.variant_id || item.id)) && !item.properties?._monster_free_gift && !String(item.sku || '').startsWith('FREE-EG25-SLOT') && (item.final_line_price > 0 || item.original_line_price > 0));
  }
  async function currentCart() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('cart-read-failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || cart.items.some((item) => !item || !Number.isSafeInteger(Number(item.variant_id || item.id)) || Number(item.variant_id || item.id) <= 0)) throw new Error('invalid-cart');
      return cart;
    } finally { clearTimeout(timer); }
  }
  async function apply(code) {
    if (!validCode(code)) throw new Error('invalid-code');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/update.js', {
        method: 'POST', credentials: 'same-origin', signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code })
      });
      if (!response.ok) throw new Error('apply-failed');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || typeof cart.item_count !== 'number' || cart.errors || cart.status >= 400) throw new Error('invalid-cart');
      const codes = cart.discount_codes;
      if (!Array.isArray(codes)) throw new Error('unverified-discount');
      if (Array.isArray(codes)) {
        const requested = codes.find((entry) => entry.code?.toUpperCase() === code);
        if (!requested || (code !== 'REVEALBACKUP' && cart.item_count > 0 && requested.applicable !== true)) throw new Error('discount-rejected');
      }
      return cart;
    } finally { clearTimeout(timer); }
  }
  function message(container, text, code, retry) {
    let box = container.querySelector('[data-sb-coupon-message]');
    if (!box) {
      box = document.createElement('div');
      box.dataset.sbCouponMessage = 'true';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      box.style.cssText = 'padding:var(--sb-space-sm,1rem);font-family:var(--sb-font-body,inherit);color:var(--sb-ink,inherit);background:var(--sb-paper,white);';
      container.append(box);
    }
    box.replaceChildren(document.createTextNode(text));
    if (code) {
      const input = document.createElement('input');
      input.readOnly = true;
      input.value = code;
      input.setAttribute('aria-label', 'Your discount code, select to copy');
      input.addEventListener('focus', () => input.select());
      input.style.cssText = 'display:block;max-width:100%;min-height:44px;';
      box.append(input);
    }
    if (retry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = 'Try applying again';
      button.style.minHeight = '44px';
      button.addEventListener('click', retry);
      box.append(button);
    }
  }
  function shoppingCTA(element) {
    if (element.dataset.sbCouponCta === 'true') return true;
    if (element.tagName === 'BUTTON' && element.textContent.trim() === 'Shop Smoke Bombs') return true;
    if (element.tagName === 'A') {
      try {
        const url = new URL(element.href, location.origin);
        return url.origin === location.origin && url.pathname === collection;
      } catch (_) { return false; }
    }
    return false;
  }
  function couponCTA(element) {
    return element.tagName === 'BUTTON' && (element.dataset.sbCouponCopyCta === 'true' || element.getAttribute('aria-label') === 'Copy coupon code');
  }
  let busy = false;
  async function activate(form, control) {
    if (busy) return;
    const code = displayedCode(form);
    if (!code) {
      message(form, 'Your code is not ready yet. Please wait for your offer, then try again.', null, () => activate(form, control));
      return;
    }
    busy = true;
    control.setAttribute('aria-busy', 'true');
    // Clipboard must start within the deliberate click, before awaiting the network.
    let copied;
    try {
      copied = navigator.clipboard?.writeText ? Promise.resolve(navigator.clipboard.writeText(code)).then(() => true, () => false) : Promise.resolve(false);
    } catch (_) { copied = Promise.resolve(false); }
    message(form, 'Applying your code before you shop...');
    try {
      await apply(code);
      if (!await copied) {
        message(form, 'Your code is applied. Copy it below if you want to keep it, then continue shopping.', code);
        const next = document.createElement('a');
        next.textContent = 'Continue shopping';
        next.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(collection);
        form.querySelector('[data-sb-coupon-message]').append(next);
      } else {
        location.assign('/discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(collection));
      }
    } catch (_) {
      message(form, 'We could not apply your code yet. Please try again before shopping. Gift tiers use merchandise after discounts.', code, () => activate(form, control));
    } finally {
      busy = false;
      control.removeAttribute('aria-busy');
    }
  }
  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('button, a');
    const form = control?.closest(scope);
    if (!form || (!shoppingCTA(control) && !couponCTA(control))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    activate(form, control);
  }, true);
  const watched = new WeakSet();
  function watch(form) {
    if (watched.has(form)) return;
    watched.add(form);
    let scheduled = false;
    const update = () => {
      scheduled = false;
      if (!displayedCode(form)) return;
      form.querySelectorAll('button, a').forEach((control) => {
        if (!visible(control)) return;
        if (couponCTA(control)) {
          control.dataset.sbCouponCopyCta = 'true';
          if (control.getAttribute('aria-label') !== 'Copy and apply coupon code, then shop') control.setAttribute('aria-label', 'Copy and apply coupon code, then shop');
          return;
        }
        if (!shoppingCTA(control)) return;
        control.dataset.sbCouponCta = 'true';
        if (control.dataset.sbCouponLabel !== 'true') {
          const labels = document.createTreeWalker(control, NodeFilter.SHOW_TEXT);
          while (labels.nextNode()) {
            if (/^shop smoke bombs$/i.test(labels.currentNode.textContent.trim())) {
              labels.currentNode.textContent = 'Copy & apply my code';
              break;
            }
          }
          control.setAttribute('aria-label', 'Copy & apply my code');
          control.dataset.sbCouponLabel = 'true';
        }
      });
    };
    new MutationObserver(() => {
      if (!scheduled) { scheduled = true; requestAnimationFrame(update); }
    }).observe(form, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden', 'value'] });
    update();
  }
  document.querySelectorAll(scope).forEach(watch);
  // Inspect only added subtrees to discover a dynamically mounted native form.
  new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.matches(scope)) watch(node);
      node.querySelectorAll(scope).forEach(watch);
    }
  }).observe(document.body, { childList: true, subtree: true });
  const url = new URL(location.href);
  const code = url.searchParams.get('sb_apply_coupon');
  if (code !== null) {
    const target = destination(url.searchParams.get('sb_coupon_redirect'));
    const container = document.createElement('section');
    container.setAttribute('aria-label', 'Apply your email offer');
    document.body.prepend(container);
    if (!validCode(code) || !target) {
      message(container, 'This offer link is incomplete. Please use the original email link or contact help@shutterbombs.com.');
    } else if (url.searchParams.has('sb_restore_cart')) {
      const rawItems = url.searchParams.get('sb_restore_cart');
      const items = restoreItems(rawItems);
      if (!rawItems.replace(/,/g, '').trim()) {
        message(container, 'Your previous items are not available in this email link. Your code is below. Apply it to your current cart or start shopping again. Free gifts use merchandise after discounts.', code);
        const current = document.createElement('button');
        current.type = 'button';
        current.textContent = 'Apply to my current cart';
        current.style.minHeight = '44px';
        current.addEventListener('click', async () => {
          if (busy) return;
          busy = true;
          try { await apply(code); location.replace('/cart'); }
          catch (_) { message(container, 'We could not apply your code to the current cart. You can still shop with the offer link below.', code); }
          finally { busy = false; }
        });
        const shop = document.createElement('a');
        shop.textContent = 'Apply code & shop smoke bombs';
        shop.href = '/cart?sb_apply_coupon=' + encodeURIComponent(code) + '&sb_coupon_redirect=' + encodeURIComponent(collection);
        shop.style.cssText = 'display:block;min-height:44px;';
        container.append(current, shop);
      } else if (!items) {
        message(container, 'We could not restore the items in this offer link. Please reopen the original cart email or contact help@shutterbombs.com.');
      } else {
        const restore = async () => {
          if (busy) return;
          busy = true;
          message(container, 'Preparing your cart with your offer...');
          try {
            const cart = await currentCart();
            if (hasPaidItems(cart)) {
              await apply(code);
              location.replace('/cart');
            } else {
              location.replace('/cart/' + items + '?storefront=true&discount=' + encodeURIComponent(code));
            }
          } catch (_) {
            message(container, 'We could not check your current cart yet. Please try again so your existing items are preserved.', code, restore);
          } finally { busy = false; }
        };
        restore();
      }
    } else {
      const run = async () => {
        if (busy) return;
        busy = true;
        message(container, 'Applying your email offer...');
        try {
          await apply(code);
          const next = new URL(target, location.origin);
          // Cart destinations let the gift drawer reconcile the discounted subtotal.
          location.replace(next.pathname + next.search + next.hash);
        }
        catch (_) { message(container, 'We could not apply your offer yet. Please try again before continuing.', code, run); }
        finally { busy = false; }
      };
      run();
    }
  }
})();
